import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Field from "@/components/Field";
import AIButton from "@/components/AIButton";
import ShareButton from "@/components/ShareButton";
import { SESSIONS, AI_ACTIONS, stageOf, isIntakeComplete } from "@/data/bridgeData";
import { getToolForActivity, toolHasContent } from "@/data/activityTools";
import { useEntries } from "@/context/EntriesContext";
import { useAuth } from "@/context/AuthContext";
import { Copy, Check, Sparkles, MessageCircle, ListChecks, Flag, FileText, ArrowRight } from "lucide-react";

function Exercise({ sessionId, ex, readOnly }) {
  const { getEntry, saveEntry } = useEntries();
  const action = AI_ACTIONS[ex.ai] || AI_ACTIONS.summarize;
  const fieldKey = `s${sessionId}:ex:${ex.id}`;
  const synthKey = `s${sessionId}:synth:${ex.id}`;
  const synth = getEntry(synthKey, "");

  return (
    <div className="rounded-sm border border-stone-200 bg-white p-7">
      <Field
        entryKey={fieldKey}
        label={ex.title}
        prompts={ex.prompts}
        rows={4}
        readOnly={readOnly}
        testid={`exercise-${ex.id}`}
      />
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <AIButton
            task={action.task}
            label={action.label}
            testid={`ai-${ex.id}`}
            getInput={() => getEntry(fieldKey, "")}
            onResult={(out) => saveEntry(synthKey, out, true)}
          />
          <ShareButton
            entryKey={fieldKey}
            label={ex.title}
            kind="reflection"
            getValue={() => getEntry(fieldKey, "")}
            testid={`share-${ex.id}`}
          />
          {synth && (
            <button onClick={() => saveEntry(synthKey, "", true)} className="text-xs text-stone-400 hover:text-stone-700 transition-colors">
              Clear synthesis
            </button>
          )}
        </div>
      )}
      {synth && (
        <div className="mt-5 rounded-sm border border-brand-sage/30 bg-brand-sage/10 p-5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.6} className="text-brand-sage" />
              <span className="label-eyebrow" style={{ color: "#697A76" }}>Assistant synthesis</span>
            </div>
            {!readOnly && (
              <ShareButton
                entryKey={synthKey}
                label={`${ex.title} — synthesis`}
                kind="synthesis"
                getValue={() => getEntry(synthKey, "")}
                testid={`share-synth-${ex.id}`}
              />
            )}
          </div>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{synth}</p>
        </div>
      )}
    </div>
  );
}

function PromptChip({ text, idx }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      data-testid={`ai-prompt-${idx}`}
      onClick={copy}
      className="group flex w-full items-start gap-3 rounded-sm border border-stone-200 bg-white p-4 text-left transition-colors duration-300 hover:border-brand-primary"
    >
      <span className="mt-0.5 text-stone-300 group-hover:text-brand-primary transition-colors">
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </span>
      <span className="text-sm text-stone-700 leading-snug">{text}</span>
    </button>
  );
}

export default function SessionPage({ readOnly = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEntry, saveEntry, loading } = useEntries();
  const session = SESSIONS.find((s) => String(s.id) === String(id));

  if (!session) {
    return <Layout><p className="text-stone-600">Session not found.</p></Layout>;
  }

  // Wait for entries before evaluating the intake gate (avoids redirect race on hard nav).
  if (loading) {
    return <Layout><div className="py-20 text-stone-400 text-sm">Loading\u2026</div></Layout>;
  }

  // Gate: participants must complete intake before entering any session.
  if (user?.role === "participant" && !isIntakeComplete(getEntry)) {
    return <Navigate to="/intake" replace />;
  }

  const stage = stageOf(session.stage);
  const isReadOnly = readOnly || user?.role === "coach" ? readOnly : false;
  const msKey = `milestone:${session.milestone.n}:done`;
  const msDone = getEntry(msKey, false);

  const Body = (
    <>
      <button onClick={() => navigate("/sessions")} className="text-sm text-stone-500 hover:text-stone-900 transition-colors mb-6">← All sessions</button>

      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <span className="label-eyebrow">Session {session.id} · Week {session.week}</span>
          <span className="inline-flex items-center gap-1.5 text-xs text-stone-500">
            <span className="h-2 w-2 rounded-full" style={{ background: stage.color }} /> {stage.name}
          </span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 leading-tight">{session.title}</h1>
        <p className="mt-4 max-w-2xl text-stone-600 leading-relaxed">{session.purpose}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-10">
          {/* Agenda */}
          <section>
            <div className="label-eyebrow mb-4">Session agenda</div>
            <ol className="space-y-2.5">
              {session.structure.map((step, i) => (
                <li key={step} className="flex gap-3 text-stone-700">
                  <span className="font-serif text-lg text-brand-primary w-6 shrink-0">{i + 1}</span>
                  <span className="leading-snug">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Exercises */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} strokeWidth={1.6} className="text-brand-primary" />
              <div className="label-eyebrow">Reflection workspace</div>
            </div>
            <div className="space-y-5">
              {session.exercises.map((ex) => (
                <Exercise key={ex.id} sessionId={session.id} ex={ex} readOnly={isReadOnly} />
              ))}
            </div>
          </section>

          {/* Between-session activities */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <ListChecks size={16} strokeWidth={1.6} className="text-brand-primary" />
              <div className="label-eyebrow">Between-session activities</div>
            </div>
            <div className="rounded-sm border border-stone-200 bg-white p-6 space-y-1">
              {session.activities.map((a, i) => {
                const key = `s${session.id}:act:${i}`;
                const done = getEntry(key, false);
                const tool = getToolForActivity(session.id, i);
                const started = tool ? toolHasContent(getEntry, tool.id) : false;
                return (
                  <div key={a} data-testid={`activity-${session.id}-${i}`} className="flex items-start gap-3 py-2 group">
                    <input
                      type="checkbox" checked={!!done} disabled={isReadOnly}
                      onChange={(e) => saveEntry(key, e.target.checked, true)}
                      className="mt-1 h-4 w-4 accent-[#8C6B5D] shrink-0"
                    />
                    <div className="flex-1">
                      <span className={`leading-snug transition-colors ${done ? "text-stone-400 line-through" : "text-stone-700"}`}>{a}</span>
                      {tool && (
                        <div className="mt-1.5">
                          <button
                            data-testid={`open-tool-${session.id}-${i}`}
                            onClick={() => navigate(`/tools/${tool.id}`)}
                            className="group/btn inline-flex items-center gap-1.5 rounded-sm border border-brand-primary/30 bg-brand-parchment/40 px-2.5 py-1 text-xs text-brand-primary transition-all duration-300 hover:bg-brand-parchment"
                          >
                            {started ? <Check size={12} className="text-brand-sage" /> : <ArrowRight size={12} className="transition-transform group-hover/btn:translate-x-0.5" />}
                            {started ? "Continue exercise" : "Open exercise"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Side column */}
        <div className="space-y-10">
          {/* Milestone */}
          <section className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Flag size={15} strokeWidth={1.6} className="text-brand-primary" />
              <div className="label-eyebrow">Milestone {session.milestone.n}</div>
            </div>
            <h4 className="font-serif text-2xl text-stone-900">{session.milestone.title}</h4>
            <p className="mt-2 text-sm text-stone-600 leading-snug">{session.milestone.desc}</p>
            <label className="mt-4 flex items-center gap-2 cursor-pointer">
              <input
                data-testid={`milestone-toggle-${session.milestone.n}`}
                type="checkbox" checked={!!msDone} disabled={isReadOnly}
                onChange={(e) => saveEntry(msKey, e.target.checked, true)}
                className="h-4 w-4 accent-[#8C6B5D]"
              />
              <span className="text-sm text-stone-700">{msDone ? "Reached" : "Mark as reached"}</span>
            </label>
          </section>

          {/* AI prompt library */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={15} strokeWidth={1.6} className="text-brand-sage" />
              <div className="label-eyebrow">AI prompt library</div>
            </div>
            <div className="space-y-2.5">
              {session.aiPrompts.map((p, i) => <PromptChip key={p} text={p} idx={i} />)}
            </div>
          </section>

          {/* Coach prompts */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle size={15} strokeWidth={1.6} className="text-brand-primary" />
              <div className="label-eyebrow">Coach prompts</div>
            </div>
            <ul className="space-y-3">
              {session.coachPrompts.map((p) => (
                <li key={p} className="border-l-2 border-stone-200 pl-4 text-[15px] italic text-stone-600 leading-snug">"{p}"</li>
              ))}
            </ul>
          </section>

          {/* Deliverables */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <FileText size={15} strokeWidth={1.6} className="text-brand-primary" />
              <div className="label-eyebrow">Deliverables</div>
            </div>
            <ul className="space-y-2">
              {session.deliverables.map((d) => (
                <li key={d} className="text-sm text-stone-600">– {d}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </>
  );

  return <Layout coachContext>{Body}</Layout>;
}
